import { type Action, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Logout } from 'flowhawk-core';

import type { ResetData } from './ApplicationActions';
import { CaseNotificationWizardStep } from '../Utils/types';

const isResetStateAction = (action: Action): action is ResetData | Logout => {
  return action.type === 'Application/RESET_DATA' || action.type === 'Auth/LOGOUT';
};

type PrevSidebarLocation = {
  pathname: string;
  search: Record<string, unknown>;
};

type ResetCaseNotificationPayload =
  | undefined
  | CaseNotificationWizardStep
  | { step?: CaseNotificationWizardStep; prevSidebarLocation?: PrevSidebarLocation };

type CaseNotificationState = {
  currentStep: CaseNotificationWizardStep;
  initialStep: CaseNotificationWizardStep;
  selectedFields: string[];
  selectedRoomIds: number[];
  include_case_tags: boolean;
  include_case_note: boolean;
  textMessage: string;
  prevSidebarLocation: PrevSidebarLocation | null;
};

const initialState: CaseNotificationState = {
  currentStep: CaseNotificationWizardStep.SelectCases,
  initialStep: CaseNotificationWizardStep.SelectCases,
  selectedFields: [],
  selectedRoomIds: [],
  include_case_tags: true,
  include_case_note: true,
  textMessage: '',
  prevSidebarLocation: null,
};

const caseNotificationsSlice = createSlice({
  name: 'caseNotifications',
  initialState,
  reducers: {
    setWizardStep(state, action: PayloadAction<CaseNotificationWizardStep>) {
      state.currentStep = action.payload;
    },
    setFields(state, action: PayloadAction<string[]>) {
      state.selectedFields = action.payload;
    },
    toggleField(state, action: PayloadAction<string | string[]>) {
      const existingFields = new Set(state.selectedFields);

      const newFields = Array.isArray(action.payload) ? action.payload : [action.payload];

      newFields.forEach((field) => {
        if (existingFields.has(field)) {
          state.selectedFields = state.selectedFields.filter((key) => key !== field);
        } else {
          state.selectedFields.push(field);
        }
      });
    },
    toggleIncludeCaseTags(state) {
      state.include_case_tags = !state.include_case_tags;
    },
    toggleIncludeCaseNote(state) {
      state.include_case_note = !state.include_case_note;
    },
    toggleRoom(state, action: PayloadAction<number>) {
      const roomId = action.payload;
      if (!state.selectedRoomIds.includes(roomId)) {
        state.selectedRoomIds.push(roomId);
      } else {
        state.selectedRoomIds = state.selectedRoomIds.filter((id) => id !== roomId);
      }
    },
    setTextMessage(state, action: PayloadAction<string>) {
      state.textMessage = action.payload;
    },

    resetCaseNotificationState(_, action: PayloadAction<ResetCaseNotificationPayload>) {
      const payload = action.payload;

      if (payload === undefined) {
        return initialState;
      }

      if (typeof payload === 'number') {
        return { ...initialState, currentStep: payload, initialStep: payload };
      }

      const step = payload.step ?? initialState.currentStep;
      return {
        ...initialState,
        currentStep: step,
        initialStep: step,
        prevSidebarLocation: payload.prevSidebarLocation ?? null,
      };
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(isResetStateAction, () => initialState);
  },
});

export const {
  setWizardStep,
  setFields,
  toggleField,
  toggleIncludeCaseTags,
  toggleIncludeCaseNote,
  toggleRoom,
  setTextMessage,
  resetCaseNotificationState,
} = caseNotificationsSlice.actions;

export default caseNotificationsSlice.reducer;
